import React, { useEffect, useRef, useState } from "react";
import Mic from "../../../Assets/icon/coaching-mic.png";
import Stroke1 from "../../../Assets/icon/Stroke1.svg";
import { ReactComponent as KeyboardIcon } from "../../../Assets/icon/keyboard.svg";
import { ReactComponent as CloseIcon } from "../../../Assets/icon/x-close-white.svg";
import { ReactComponent as PauseIcon } from "../../../Assets/icon/Pause.svg";
import { ReactComponent as PlayIcon } from "../../../Assets/icon/Play-voice.svg";
import "../style.css";
import "../../CoachV2/styles.css";
import { setVoiceWalkthrough } from "../../../Services/userSettings";

const SpeechControls = ({
  recordingMode,
  handsFreeMode,
  arisDisconnected,
  error,
  secondsRef,
  disconnectConversation,
  disconnectText,
  timeoutText,
  walkthroughEnabled = false,
  onKeyboardSwitchClick,
  showEndSessionButton = false,
  onToggleSession,
  isSessionActive,
  onOpenMinutesModal,
  partnerName,
  isGroup,
  isPaused = false,
  onPause,
  onResume,
}) => {
  const [walkStep, setWalkStep] = useState(-1); // -1 off, 1 mic
  const micRef = useRef(null);
  const endSessionRef = useRef(null);


  useEffect(() => {
    if (!walkthroughEnabled) return;
    let done = false;
    try { done = localStorage.getItem("voice_walkthrough_done") === "true"; } catch (_) { }
    if (!done) {
      const t = setTimeout(() => setWalkStep(1), 500);
      return () => clearTimeout(t);
    } else {
      setWalkStep(-1);
    }
  }, [walkthroughEnabled]);

  useEffect(() => {
    if (walkStep <= 0) return;
    const el = micRef.current;
    if (!el) return;
  }, [walkStep]);

  const finishWalkthrough = async () => {
    setWalkStep(-1);
    try { localStorage.setItem("voice_walkthrough_done", "true"); } catch (_) { }
    try { await setVoiceWalkthrough(true); } catch (_) { }
  };

  const isWalk = walkStep > 0;
  const focusMic = walkStep === 1;

  const arrowLeft = '50%';

  // Modal managed by parent (ArisCoach)

  // Check if user is out of minutes
  const isOutOfMinutes = secondsRef.current <= 0;

  return (
    <div className={`coaching-speech-controls-container ${recordingMode && 'recording'} ${(disconnectText || error || timeoutText) && 'disable'}`}>
      <div className="coaching-speech-controls">
        {(recordingMode || handsFreeMode) && (
          <div className="coaching-stroke-container">
            <img src={Stroke1} alt="Stroke Layer 1" className="coaching-stroke-image1" draggable='false' style={{ WebkitTouchCallout: 'none', userSelect: 'none' }} />
            <img src={Stroke1} alt="Stroke Layer 2" className="coaching-stroke-image2" draggable='false' style={{ WebkitTouchCallout: 'none', userSelect: 'none' }} />
            <img src={Stroke1} alt="Stroke Layer 3" className="coaching-stroke-image3" draggable='false' style={{ WebkitTouchCallout: 'none', userSelect: 'none' }} />
            <img src={Stroke1} alt="Stroke Layer 4" className="coaching-stroke-image4" draggable='false' style={{ WebkitTouchCallout: 'none', userSelect: 'none' }} />
          </div>
        )}
        <div className="coaching-speech-controls-top">
          {/* Left: End session button (only when active) */}
          {showEndSessionButton && !isOutOfMinutes && (
            <div
              ref={endSessionRef}
              className="coaching-end-session-container"
              id="end-session-button"
              onClick={() => {
                if (!isWalk && typeof onToggleSession === 'function') {
                  onToggleSession();
                }
              }}
            >
              <CloseIcon />
            </div>
          )}

          {/* Center: Mic button (start) or Pause/Resume (when active) */}
          {!isOutOfMinutes && (
            <div
              ref={micRef}
              className={`coaching-mic-container ${isWalk && !focusMic ? 'sc-walk-dim' : ''} ${focusMic ? 'sc-walk-focus' : ''}`}
            >
              <div
                className={`coaching-mic ${recordingMode ? 'recording' : ''}`}
                onClick={() => {
                  if (isWalk || error !== null || secondsRef.current <= 0) return;

                  // When no active session, start session
                  if (!isSessionActive) {
                    if (typeof onToggleSession === 'function') {
                      onToggleSession();
                    }
                    return;
                  }

                  // When session is active, toggle pause/resume
                  if (isPaused) {
                    if (typeof onResume === 'function') {
                      onResume();
                    }
                  } else {
                    if (typeof onPause === 'function') {
                      onPause();
                    }
                  }
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                {!isSessionActive ? (
                  <img
                    src={Mic}
                    alt="Mic"
                    width={32}
                    height={32}
                    draggable={false}
                    unselectable="on"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ WebkitTouchCallout: 'none', userSelect: 'none' }}
                  />
                ) : isPaused ? (
                  <PlayIcon width={24} height={24} />
                ) : (
                  <PauseIcon width={24} height={24} />
                )}
              </div>
            </div>
          )}

          {showEndSessionButton && !isOutOfMinutes && (
            <div className="coaching-session-container-dummy">
            </div>
          )}
        </div>
        {!isOutOfMinutes && (
          <div className="coaching-speech-controls-bottom">
            {/* Show labels based on button state */}
            {!isSessionActive ? (
              <div className={`speak-text`}>
                {isGroup && partnerName ? `Start Session with ${partnerName}` : 'Start Session'}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3rem' }}>
                {/* Left: End label */}
                {showEndSessionButton && (
                  <div className={`speak-text`} style={{ width: '48px', textAlign: 'center' }}>
                    End
                  </div>
                )}
                {/* Center: Pause/Resume label */}
                <div className={`speak-text`} style={{ width: '68px', textAlign: 'center' }}>
                  {isPaused ? 'Resume' : 'Pause'}
                </div>
                {/* Right: Dummy spacer for alignment */}
                {showEndSessionButton && (
                  <div style={{ width: '48px' }}></div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="coaching-speech-controls-background" />

      {/* Walkthrough overlay and bubbles */}
      {isWalk && <div className="coachv2-mask" />}
      {walkStep === 1 && (
        <div className="sc-walk-bubble sc-walk-bubble-mic" style={{ '--bubble-arrow-left': arrowLeft }}>
          <div className="sc-walk-desc">Click the <strong>mic button</strong> to start your voice dialogue session with Aris. During the session, use this button to pause or resume.</div>
          <div className="sc-walk-row">
            <div className="sc-walk-dots">
              <span className="sc-walk-dot active" />
            </div>
            <button className="sc-walk-next" onClick={finishWalkthrough}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeechControls;